// Bed transfer history: the audit trail written by the bed-transfer requisition.
//
// THREE transfer types appear here, not two. New rows are within_ward or cross_ward;
// "cross_department" persists on rows written before the bed/department decoupling and
// is never created again. Historical rows keep the type they were actually recorded
// under — an audit trail should say what happened, not what today's vocabulary would
// call it — so this screen must go on rendering all three.
import React, { useEffect, useMemo, useState } from "react";

import {
  Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, MenuItem, Typography, LinearProgress,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";

import { useNurseData, dateFilterParams } from "../NurseDataContext";
import { NURSE_COLORS } from "../theme";
import { getTransferLog, listOf } from "../api/nurseApi";
import { istDayKey, formatIstTime } from "../../../utils/istDateTime";

// Anything not in this map renders its raw value rather than being silently
// mislabelled as one of the known types.
const TRANSFER_TYPE_LABEL = {
  within_ward: "within-ward",
  cross_ward: "cross-ward",
  cross_department: "cross-dept (legacy)",
};

// transferred_at comes off main_patient_bed_transfer_audit, a naive-UTC column
// — slicing its raw digits dated every transfer 5h30m early.
const fmt = (dt) => {
  const day = istDayKey(dt);
  return day ? `${day} ${formatIstTime(dt)}` : "—";
};
const bedLabel = (code, location) => [location, code].filter(Boolean).join(" · ") || "—";

export default function TransferHistory() {
  const { dateFrom, dateTo } = useNurseData();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeF, setTypeF] = useState("all");

  const dateParams = useMemo(() => dateFilterParams(dateFrom, dateTo), [dateFrom, dateTo]);

  useEffect(() => {
    setLoading(true);
    getTransferLog(dateParams).then((r) => setRows(listOf(r))).catch(() => {}).finally(() => setLoading(false));
  }, [dateParams]);

  const filtered = useMemo(
    () => rows.filter((t) => typeF === "all" || t.transfer_type === typeF),
    [rows, typeF]
  );

  return (
    <Box>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <HistoryIcon sx={{ color: NURSE_COLORS.primary }} />
        <Typography variant="h6">Bed Transfer History</Typography>
        <Chip size="small" label={rows.length} sx={{ ml: 1 }} />
      </Box>
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField select size="small" label="Type" value={typeF} onChange={(e) => setTypeF(e.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="within_ward">Within ward</MenuItem>
            <MenuItem value="cross_ward">Cross ward</MenuItem>
            <MenuItem value="cross_department">Cross department (legacy)</MenuItem>
          </TextField>
        </Stack>
      </Paper>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Paper sx={{ borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}`, overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
              <TableCell>When</TableCell><TableCell>Patient</TableCell><TableCell>From</TableCell><TableCell>To</TableCell><TableCell>Type</TableCell><TableCell>Reason</TableCell><TableCell>By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((t) => (
              <TableRow key={t.id} hover>
                <TableCell>{fmt(t.transferred_at)}</TableCell>
                <TableCell>{t.patient_name || t.patient_id || "—"}</TableCell>
                <TableCell>{bedLabel(t.from_bed_code, t.from_location)}</TableCell>
                <TableCell>{bedLabel(t.to_bed_code, t.to_location)}</TableCell>
                <TableCell>
                  <Chip size="small" label={TRANSFER_TYPE_LABEL[t.transfer_type] || t.transfer_type || "—"} color={t.transfer_type === "within_ward" ? "default" : "warning"} variant="outlined" />
                </TableCell>
                <TableCell>{t.reason || "—"}</TableCell>
                <TableCell>{t.transferred_by_name || "—"}</TableCell>
              </TableRow>
            ))}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>No transfers yet.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
